import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

/**
 * Button — single primitive used everywhere in the app. Variants follow
 * the visual language of desktop-foundations.html:
 *   - primary: filled royal-blue, used for the principal action of a screen
 *   - secondary: subtle pale-blue background, used for "ok-but-not-the-main"
 *   - ghost: transparent, used in toolbars and table row actions
 *   - danger: peach for destructive ops
 *
 * Sizes: sm (table rows), md (default), lg (login submit, FAB-equivalent).
 *
 * `asChild` lets callers swap the rendered element via Radix Slot — the
 * canonical use case is wrapping a router <Link> while keeping button
 * semantics + styles.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-sm font-medium transition outline-none focus-visible:ring-2 focus-visible:ring-brand-pale disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-brand-primary text-white hover:brightness-105',
        secondary: 'bg-brand-pale text-brand-primary hover:bg-brand-soft/40',
        ghost: 'text-ink-800 hover:bg-surface-sunken',
        danger: 'bg-peach-500 text-white hover:brightness-105',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-11 px-5 text-base',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
  );
});
